import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Wifi, WifiOff, ServerCrash, ShieldAlert, Activity, PlugZap, CheckCircle2, PhoneCall } from 'lucide-react';

// Campanella delle notifiche operative (richiesta dell'utente: "come sul CRM"): broker offline,
// asset critico irraggiungibile, nuovo incident critical, anomalie di data quality,
// integrazioni in errore. Le genera e chiude da solo il backend (jobs/notifications.ts); qui
// si leggono ogni minuto, si segnano lette e si apre la pagina giusta.
export interface Notification {
  id: number;
  kind: 'broker_offline' | 'critical_asset' | 'critical_asset_recovered' | 'critical_incident' | 'data_quality' | 'sync_error' | 'phone_call';
  severity: 'info' | 'warning' | 'critical';
  title: string;
  message: string;
  link: string | null;
  created_at: string;
  resolved_at: string | null;
  tenant_name: string | null;
  read: boolean;
}

export const KIND_ICON: Record<Notification['kind'], React.ReactNode> = {
  broker_offline: <WifiOff size={13} />,
  critical_asset: <ServerCrash size={13} />,
  critical_asset_recovered: <Wifi size={13} />,
  critical_incident: <ShieldAlert size={13} />,
  data_quality: <Activity size={13} />,
  sync_error: <PlugZap size={13} />,
  phone_call: <PhoneCall size={13} />,
};
export const SEVERITY_COLOR: Record<Notification['severity'], string> = {
  critical: 'bg-red-100 text-red-600', warning: 'bg-amber-100 text-amber-700', info: 'bg-gray-100 text-gray-500',
};

export function timeAgo(iso: string): string {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get('/api/notifications');
      setItems(res.data.data);
      setUnread(res.data.meta?.unread ?? 0);
    } catch { /* la campanella non deve mai rompere la pagina */ }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  async function markRead(ids?: number[]) {
    await axios.post('/api/notifications/read', ids ? { ids } : {});
    setItems(list => list.map(n => (!ids || ids.includes(n.id) ? { ...n, read: true } : n)));
    setUnread(u => (ids ? Math.max(0, u - ids.filter(id => items.find(n => n.id === id && !n.read && !n.resolved_at)).length) : 0));
  }

  function openItem(n: Notification) {
    if (!n.read) markRead([n.id]);
    setOpen(false);
    if (n.link) navigate(n.link);
  }

  const active = items.filter(n => !n.resolved_at);
  const resolved = items.filter(n => n.resolved_at);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => { setOpen(o => !o); if (!open) load(); }} title="Notifications"
        className="relative p-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-800 transition-colors">
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden text-left">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="font-semibold text-gray-900 text-sm">Notifications {active.length > 0 && <span className="text-gray-400 font-normal">· {active.length} open</span>}</span>
            {unread > 0 && (
              <button onClick={() => markRead()} className="flex items-center gap-1 text-xs text-primary-600 hover:underline">
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-400">
              <Bell size={24} className="mx-auto mb-2 opacity-30" /> Nothing to report
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
              {[...active, ...resolved].map(n => (
                <div key={n.id} onClick={() => openItem(n)}
                  className={`px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${!n.read && !n.resolved_at ? 'bg-blue-50/50' : ''} ${n.resolved_at ? 'opacity-60' : ''}`}>
                  <div className="flex items-start gap-2">
                    <span className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${n.resolved_at ? 'bg-emerald-100 text-emerald-600' : SEVERITY_COLOR[n.severity]}`}>
                      {n.resolved_at ? <CheckCircle2 size={13} /> : KIND_ICON[n.kind]}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs truncate ${!n.read && !n.resolved_at ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>{n.title}</p>
                      <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[11px] text-gray-400 mt-1">
                        {n.resolved_at ? `resolved ${timeAgo(n.resolved_at)} · opened ${timeAgo(n.created_at)}` : timeAgo(n.created_at)}
                      </p>
                    </div>
                    {!n.read && !n.resolved_at && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 shrink-0" />}
                  </div>
                </div>
              ))}
            </div>
          )}
          <button onClick={() => { setOpen(false); navigate('/notifications'); }}
            className="block w-full px-4 py-2.5 border-t border-gray-100 text-sm font-medium text-primary-600 hover:bg-gray-50">
            Open all notifications
          </button>
          <div className="px-4 py-2 border-t border-gray-100 text-[11px] text-gray-400">
            Broker offline, critical assets unreachable, new critical incidents, data quality issues and failing integrations. Items close by themselves when the condition clears.
          </div>
        </div>
      )}
    </div>
  );
}
